import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";

import type { AdminSession } from "@/features/coaching/models";
import { adminSessionSchema } from "@/features/coaching/models";

interface EditSessionContextValue {
  form: UseFormReturn<Omit<AdminSession, "coach">>;
}

const EditSessionContext = createContext<EditSessionContextValue | null>(null);

type EditSessionContextProviderProps = PropsWithChildren & {
  session: Omit<AdminSession, "coach">;
};

export const EditSessionContextProvider = ({
  session,
  children,
}: EditSessionContextProviderProps) => {
  const form = useForm({
    resolver: zodResolver(adminSessionSchema.omit({ coach: true })),
    defaultValues: session,
  });

  return (
    <EditSessionContext.Provider value={{ form }}>
      {children}
    </EditSessionContext.Provider>
  );
};

export const useEditSessionContext = () => {
  const context = useContext(EditSessionContext);

  if (!context) {
    throw new Error(
      "useEditSessionContext must be used within a <EditSessionContextProvider />",
    );
  }

  return context;
};
